/**
 * Impersonation state as the app reads it (ClickUp 86e39gctg).
 *
 * A superuser starts impersonating from Django admin; the backend keeps it in
 * the admin's own session and reports it on `GET /auth/me` as
 * `impersonation: {active, impersonator: {id, name}, started_at,
 * target: {id, name, email}, tenant: {id, name, subdomain}}` (absent or
 * `{active: false}` otherwise); the backend also sends `session_id`,
 * `impersonator.email` and `role` (the target's role in that tenant). While it is active the backend pins the
 * session's tenant to `impersonation.tenant`, so the app does too.
 *
 * No imports from authSlice.js: it imports this module, and components read
 * these selectors without pulling in the slice (tests mock the slice).
 */

/**
 * @typedef {Object} Impersonation
 * @property {true} active
 * @property {{id: string, name: string}} impersonator
 * @property {string|null} startedAt
 * @property {{id: string, name: string, email: string}} target
 * @property {{id: string, name: string, subdomain: string}} tenant
 * @property {string|null} role - the target's role in `tenant`, as the backend pinned it
 */

/** An id as the app compares ids: JSON:API ids are strings. */
function idString(value) {
  return value == null || value === '' ? null : String(value)
}

/**
 * The `/auth/me` impersonation block, validated. Anything but an active block
 * naming a tenant is "not impersonating": without a tenant there is nothing
 * to pin, and the backend would not report one active without it.
 *
 * @param {unknown} raw
 * @returns {Impersonation|null}
 */
export function normalizeImpersonation(raw) {
  if (!raw || typeof raw !== 'object' || raw.active !== true) return null
  const tenantId = idString(raw.tenant?.id)
  if (!tenantId) return null
  return {
    active: true,
    impersonator: { id: idString(raw.impersonator?.id), name: raw.impersonator?.name || '' },
    startedAt: raw.started_at || null,
    target: {
      id: idString(raw.target?.id),
      name: raw.target?.name || raw.target?.email || '',
      email: raw.target?.email || '',
    },
    tenant: { id: tenantId, name: raw.tenant?.name || '', subdomain: raw.tenant?.subdomain || '' },
    role: typeof raw.role === 'string' && raw.role ? raw.role : null,
  }
}

/**
 * The pinned tenant as a tenants-adapter entity, for when it is not one of
 * the payload's own tenants.
 *
 * @param {Impersonation} impersonation
 * @returns {{id: string, name: string, subdomain: string}}
 */
export function impersonatedTenantEntity(impersonation) {
  const { id, name, subdomain } = impersonation.tenant
  return { id, name, subdomain }
}

/**
 * @param {{auth?: {impersonation?: Impersonation|null}}} state
 * @returns {Impersonation|null}
 */
export const selectImpersonation = (state) => state.auth?.impersonation ?? null

/**
 * @param {{auth?: {impersonation?: Impersonation|null}}} state
 * @returns {boolean}
 */
export const selectIsImpersonating = (state) => selectImpersonation(state)?.active === true

/** A path segment that names one record: a UUID or a numeric id. */
const RECORD_ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/**
 * Where to land after exiting. The current route is kept when the tenant is
 * unchanged, and also when it changed but the route names no record (a list
 * or settings page exists in every tenant). A record the impersonated tenant
 * owns does not resolve in the admin's own tenant, so that lands on `/`.
 *
 * @param {{pathname: string, search?: string, hash?: string}} location
 * @param {{tenantChanged: boolean}} options
 * @returns {string}
 */
export function exitLandingPath({ pathname, search = '', hash = '' }, { tenantChanged }) {
  const namesRecord = pathname.split('/').some((segment) => RECORD_ID_SEGMENT.test(segment))
  if (tenantChanged && namesRecord) return '/'
  return `${pathname}${search}${hash}`
}
